// src/pages/chat/ChatEditPage.tsx

import { useEffect, useState, useRef, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import type { Conversation, ConversationMember, ConversationRole, ConversationSettings, MentionTag } from '@core/types/chat';
import { userprofileApi, chatApi } from '@core/api';
import {
  Button,
  Input,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  Alert,
  Badge,
  ErrorAlert,
} from '@react/components';
import { Select } from '@/components/Select';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { UserPicker } from '@/components/UserPicker';
import { EntityAvatar } from '@/components/EntityAvatar';
import { FormRow, AutoGrowTextarea } from '@/components/form';
import {
  loadConversation,
  updateConversation,
  updateConversationSettings,
  validateUpdateConversation,
  loadMembers,
  addMember,
  removeMember,
  updateMemberRole,
  leaveConversation,
  archiveConversation,
  deleteConversation,
  memberFromPick,
  uploadAttachment,
  getAttachmentDownloadUrl,
  roleLabelKeyMap,
  roleBadgeVariantMap,
  editableRoles,
  threadPolicyOptions,
} from '@/features/chat/logic';

interface EnrichedProfile {
  displayName: string;
  avatarUrl: string;
}

export const ChatEditPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useI18n();

  // Conversation state
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Form state
  const [formData, setFormData] = useState({ name: '', description: '', avatar_url: '' });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Settings state
  const [settings, setSettings] = useState<ConversationSettings>({
    members_can_post: true,
    members_can_add_members: true,
    members_can_pin_messages: true,
    mute_by_default: false,
    threads_enabled: true,
    thread_policy: 'THREAD_POLICY_ALLOWED',
  });
  const [isSavingSettings, setIsSavingSettings] = useState(false);

  // Members state
  const [members, setMembers] = useState<ConversationMember[]>([]);
  const [isLoadingMembers, setIsLoadingMembers] = useState(true);
  const [profileMap, setProfileMap] = useState<Map<string, EnrichedProfile>>(new Map());
  const [isAdding, setIsAdding] = useState(false);
  const [editingRoleFor, setEditingRoleFor] = useState<string | null>(null);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const avatarFileRef = useRef<HTMLInputElement>(null);

  // Mention tags state (chat.CreateMentionTag / ListMentionTags / DeleteMentionTag)
  const [mentionTags, setMentionTags] = useState<MentionTag[]>([]);
  const [isLoadingTags, setIsLoadingTags] = useState(true);
  const [newTagName, setNewTagName] = useState('');
  const [newTagDescription, setNewTagDescription] = useState('');
  const [isCreatingTag, setIsCreatingTag] = useState(false);

  // Confirm dialog state — single slot, supports destructive variant and
  // async confirm. Closes on success or explicit cancel.
  const [confirmDialog, setConfirmDialog] = useState<{
    title: string;
    description?: string;
    confirmLabel?: string;
    destructive?: boolean;
    onConfirm: () => void | Promise<void>;
  } | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);

  // ---- Load data ----

  const loadData = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    setError(null);
    try {
      const conv = await loadConversation(id);
      setConversation(conv);
      setFormData({
        name: conv.name ?? '',
        description: conv.description ?? '',
        avatar_url: conv.avatar_url ?? '',
      });
      if (conv.settings) {
        setSettings(conv.settings);
      }
    } catch (err) {
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, [id, t]);

  const loadMemberList = useCallback(async () => {
    if (!id) return;
    setIsLoadingMembers(true);
    try {
      const m = await loadMembers(id);
      setMembers(m);

      // Enrich with full profile data (display names + avatars). Batch-fetch
      // UserProfiles for all members in one call; seed the map from raw
      // member data first so fallback names render even if enrichment fails.
      const map = new Map<string, EnrichedProfile>();
      for (const mem of m) {
        const pid = mem.profile?.entity?.id;
        if (pid) {
          map.set(pid, {
            displayName: mem.profile?.display_name || '',
            avatarUrl: mem.profile?.avatar ?? '',
          });
        }
      }
      const profileIds = m
        .map(mem => mem.profile?.entity?.id)
        .filter((pid): pid is string => !!pid);
      if (profileIds.length > 0) {
        try {
          const res = await userprofileApi.ListUserprofilesByIDs({ ids: profileIds });
          for (const p of res.userprofiles?.profiles ?? []) {
            if (!p.entity?.id) continue;
            const name = p.display_name?.trim()
              || [p.first_name, p.last_name].filter(Boolean).join(' ').trim()
              || p.username?.trim()
              || '';
            // Backend packs full URL in avatar.title, thumbnail in avatar.icon.
            const avatarUrl = p.avatar?.icon?.trim() || p.avatar?.title?.trim() || '';
            const existing = map.get(p.entity.id);
            map.set(p.entity.id, {
              displayName: name || existing?.displayName || '',
              avatarUrl: avatarUrl || existing?.avatarUrl || '',
            });
          }
        } catch {
          // keep fallback names
        }
      }
      setProfileMap(map);
    } catch {
      // silent
    } finally {
      setIsLoadingMembers(false);
    }
  }, [id]);

  const loadMentionTags = useCallback(async () => {
    if (!id) return;
    setIsLoadingTags(true);
    try {
      const res = await chatApi.ListMentionTags(id);
      setMentionTags(res.mention_tags?.items ?? []);
    } catch {
      // silent
    } finally {
      setIsLoadingTags(false);
    }
  }, [id]);

  useEffect(() => {
    loadData();
    loadMemberList();
    loadMentionTags();
  }, [loadData, loadMemberList, loadMentionTags]);

  // Auto-clear success message
  useEffect(() => {
    if (!success) return;
    const timer = setTimeout(() => setSuccess(null), 3000);
    return () => clearTimeout(timer);
  }, [success]);

  // ---- Handlers ----

  const showSuccess = (key: string) => setSuccess(t(key));

  const handleChange = (field: keyof typeof formData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setFormErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleAvatarFileSelected = async (file: File | null) => {
    if (!file) return;
    setIsUploadingAvatar(true);
    setError(null);
    try {
      const { entityId } = await uploadAttachment(file);
      const url = await getAttachmentDownloadUrl(entityId);
      if (url) {
        setFormData(prev => ({ ...prev, avatar_url: url }));
        showSuccess('chat.saveSuccess');
      }
    } catch (err) {
      setError(err);
    } finally {
      setIsUploadingAvatar(false);
      if (avatarFileRef.current) avatarFileRef.current.value = '';
    }
  };

  const handleSaveInfo = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !conversation) return;

    const errors = validateUpdateConversation(formData);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setIsSubmitting(true);
    setError(null);
    try {
      const updated = await updateConversation(id, conversation, formData);
      setConversation(updated);
      showSuccess('chat.saveSuccess');
    } catch (err) {
      setError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveSettings = async () => {
    if (!id || !conversation) return;
    setIsSavingSettings(true);
    setError(null);
    try {
      const updated = await updateConversationSettings(id, conversation, settings);
      setConversation(updated);
      showSuccess('chat.saveSuccess');
    } catch (err) {
      setError(err);
    } finally {
      setIsSavingSettings(false);
    }
  };

  const handleSettingChange = (key: keyof ConversationSettings, value: boolean | string | number) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handlePickMember = async (userId: string, displayName: string) => {
    if (!id || !userId) return;
    if (members.some(m => m.profile?.entity?.id === userId)) return;
    setIsAdding(true);
    try {
      await addMember(id, memberFromPick(userId, displayName));
      showSuccess('chat.memberAdded');
      loadMemberList();
    } catch (err) {
      setError(err);
    } finally {
      setIsAdding(false);
    }
  };

  const handleRemoveMember = (member: ConversationMember) => {
    if (!id || !member.profile?.entity?.id) return;
    const name = member.profile?.display_name ?? member.profile?.entity?.id;
    setConfirmDialog({
      title: t('chat.removeMemberConfirm', { name }),
      destructive: true,
      confirmLabel: t('common.remove'),
      onConfirm: async () => {
        try {
          await removeMember(id, member.profile!.entity!.id!);
          showSuccess('chat.memberRemoved');
          loadMemberList();
        } catch (err) {
          setError(err);
        }
      },
    });
  };

  const handleUpdateRole = async (member: ConversationMember, role: ConversationRole) => {
    if (!id || !member.profile?.entity?.id) return;
    try {
      await updateMemberRole(id, member.profile.entity.id, role);
      setEditingRoleFor(null);
      showSuccess('chat.roleUpdated');
      loadMemberList();
    } catch (err) {
      setError(err);
    }
  };

  const handleCreateMentionTag = async () => {
    if (!id || !newTagName.trim()) return;
    setIsCreatingTag(true);
    try {
      await chatApi.CreateMentionTag(id, {
        name: newTagName.trim(),
        description: newTagDescription.trim() || undefined,
      });
      setNewTagName('');
      setNewTagDescription('');
      showSuccess('chat.mentionTags.createSuccess');
      loadMentionTags();
    } catch (err) {
      setError(err);
    } finally {
      setIsCreatingTag(false);
    }
  };

  const handleDeleteMentionTag = (tag: MentionTag) => {
    if (!id || !tag.entity?.id) return;
    setConfirmDialog({
      title: t('chat.mentionTags.deleteConfirm', { name: tag.name }),
      destructive: true,
      confirmLabel: t('common.delete'),
      onConfirm: async () => {
        try {
          await chatApi.DeleteMentionTag(id, tag.entity!.id!);
          setMentionTags(prev => prev.filter(mt => mt.entity?.id !== tag.entity?.id));
          showSuccess('chat.mentionTags.deleteSuccess');
        } catch (err) {
          setError(err);
        }
      },
    });
  };

  const handleLeave = () => {
    if (!id) return;
    setConfirmDialog({
      title: t('chat.leaveConfirm'),
      destructive: true,
      confirmLabel: t('chat.leave'),
      onConfirm: async () => {
        try {
          await leaveConversation(id);
          navigate('/chats');
        } catch (err) {
          setError(err);
        }
      },
    });
  };

  const handleArchive = () => {
    if (!id) return;
    setConfirmDialog({
      title: t('chat.archiveConfirm'),
      confirmLabel: t('chat.archive'),
      onConfirm: async () => {
        try {
          await archiveConversation(id);
          showSuccess('chat.archived');
          loadData();
        } catch (err) {
          setError(err);
        }
      },
    });
  };

  const handleDelete = () => {
    if (!id) return;
    setConfirmDialog({
      title: t('chat.deleteConfirm'),
      destructive: true,
      confirmLabel: t('common.delete'),
      onConfirm: async () => {
        try {
          await deleteConversation(id);
          navigate('/chats');
        } catch (err) {
          setError(err);
        }
      },
    });
  };

  // ---- Render ----

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">{t('common.loading')}</div>
      </div>
    );
  }

  if (!conversation && !error) {
    return (
      <Alert variant="warning" title={t('common.notFound')}>
        <Link to="/chats" className="mt-2 inline-block text-sm underline text-primary">
          {t('common.backToList')}
        </Link>
      </Alert>
    );
  }

  const isDirect = conversation?.type === 'CONVERSATION_TYPE_DIRECT';

  return (
    <div className="mx-auto max-w-2xl space-y-6 pb-12">
      {/* Header */}
      <div>
        <nav className="text-sm mb-2">
          <Link to="/chats" className="text-muted-foreground hover:text-foreground">
            {t('chat.list')}
          </Link>
          <span className="mx-2 text-muted-foreground">/</span>
          {conversation && (
            <>
              <Link to={`/chats/${id}`} className="text-muted-foreground hover:text-foreground">
                {conversation.name || t('chat.untitled')}
              </Link>
              <span className="mx-2 text-muted-foreground">/</span>
            </>
          )}
          <span className="text-foreground">{t('common.edit')}</span>
        </nav>
        {/* The tab already names this page; the heading stays for screen readers. */}
        <h1 className="sr-only">{t('chat.edit')}</h1>
      </div>

      {error ? (
        <ErrorAlert error={error} dismissible onDismiss={() => setError(null)} />
      ) : null}
      {success && (
        <Alert variant="success" dismissible onDismiss={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* ---- Conversation Info ---- */}
      <form onSubmit={handleSaveInfo}>
        <Card>
          <CardHeader>
            <CardTitle>{t('chat.details')}</CardTitle>
            <CardDescription>{t('chat.updateInfo')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Avatar: click to upload, hover overlay to change */}
            <div className="flex items-start gap-4">
              <div className="relative flex-shrink-0 group">
                <EntityAvatar name={formData.name || conversation?.name} image={formData.avatar_url} className="h-20 w-20 text-xl" />
                <button
                  type="button"
                  onClick={() => avatarFileRef.current?.click()}
                  disabled={isUploadingAvatar}
                  className="absolute inset-0 flex items-center justify-center rounded-full bg-background/80 text-xs font-medium text-foreground opacity-0 transition-opacity group-hover:opacity-100 disabled:opacity-50"
                  title={t('chat.uploadAvatar')}
                >
                  {isUploadingAvatar ? (
                    <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                  ) : (
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6.827 6.175A2.31 2.31 0 0 1 5.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 0 0-1.134-.175 2.31 2.31 0 0 1-1.64-1.055l-.822-1.316a2.192 2.192 0 0 0-1.736-1.039 48.774 48.774 0 0 0-5.232 0 2.192 2.192 0 0 0-1.736 1.039l-.821 1.316ZM16.5 12.75a4.5 4.5 0 1 1-9 0 4.5 4.5 0 0 1 9 0Z" />
                    </svg>
                  )}
                </button>
                <input
                  ref={avatarFileRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={e => handleAvatarFileSelected(e.target.files?.[0] ?? null)}
                />
              </div>
              <div className="flex-1 space-y-1">
                <FormRow label={t('chat.avatarUrl')} labelWidth="sm" hint={t('chat.avatarUploadHint')}>
                  <Input
                    type="url"
                    value={formData.avatar_url}
                    onChange={e => handleChange('avatar_url', e.target.value)}
                    placeholder="https://..."
                    aria-label={t('chat.avatarUrl')}
                  />
                </FormRow>
              </div>
            </div>

            <FormRow label={t('chat.create.nameLabel')} labelWidth="sm">
              <Input
                type="text"
                value={formData.name}
                onChange={e => handleChange('name', e.target.value)}
                placeholder={t('chat.create.groupNamePlaceholder')}
                error={formErrors.name ? t(formErrors.name) : undefined}
                aria-label={t('chat.create.nameLabel')}
                required
              />
            </FormRow>
            <FormRow label={t('chat.create.descriptionLabel')} labelWidth="sm">
              <AutoGrowTextarea
                value={formData.description}
                onChange={value => handleChange('description', value)}
                placeholder={t('chat.create.descriptionPlaceholder')}
                minRows={2}
                aria-label={t('chat.create.descriptionLabel')}
              />
            </FormRow>
          </CardContent>
          <CardFooter className="flex justify-end">
            <Button type="submit" disabled={isSubmitting} isLoading={isSubmitting}>
              {t('common.saveChanges')}
            </Button>
          </CardFooter>
        </Card>
      </form>

      {/* ---- Settings ---- */}
      {!isDirect && (
        <Card>
          <CardHeader>
            <CardTitle>{t('chat.settingsTitle')}</CardTitle>
            <CardDescription>{t('chat.settingsDescription')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <ToggleRow
              label={t('chat.settings.membersCanPost')}
              description={t('chat.settings.membersCanPostDesc')}
              checked={settings.members_can_post}
              onChange={v => handleSettingChange('members_can_post', v)}
            />
            <ToggleRow
              label={t('chat.settings.membersCanAddMembers')}
              description={t('chat.settings.membersCanAddMembersDesc')}
              checked={settings.members_can_add_members}
              onChange={v => handleSettingChange('members_can_add_members', v)}
            />
            <ToggleRow
              label={t('chat.settings.membersCanPin')}
              description={t('chat.settings.membersCanPinDesc')}
              checked={settings.members_can_pin_messages}
              onChange={v => handleSettingChange('members_can_pin_messages', v)}
            />

            <div className="border-t border-border pt-4" />

            <ToggleRow
              label={t('chat.settings.threadsEnabled')}
              description={t('chat.settings.threadsEnabledDesc')}
              checked={settings.threads_enabled}
              onChange={v => handleSettingChange('threads_enabled', v)}
            />
            {settings.threads_enabled && (
              <div className="ml-6">
                <Select
                  label={t('chat.settings.threadPolicy')}
                  value={settings.thread_policy ?? 'THREAD_POLICY_ALLOWED'}
                  onChange={e => handleSettingChange('thread_policy', e.target.value)} options={threadPolicyOptions.map(o => ({ value: o.value, label: t(o.labelKey) }))} />
              </div>
            )}
          </CardContent>
          <CardFooter className="flex justify-end">
            <Button
              onClick={handleSaveSettings}
              disabled={isSavingSettings}
              isLoading={isSavingSettings}
            >
              {t('common.saveChanges')}
            </Button>
          </CardFooter>
        </Card>
      )}

      {/* ---- Members ---- */}
      <Card>
        <CardHeader>
          <CardTitle>{t('chat.members')} ({members.length})</CardTitle>
          <CardDescription>{t('chat.manageMembersDescription')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Add member — same picker used everywhere else (docs/ux/14-forms.md) */}
          <UserPicker
            value=""
            placeholder={t('chat.create.searchPeoplePlaceholder')}
            onChange={handlePickMember}
            selectedIds={members.map(m => m.profile?.entity?.id ?? '')}
          />
          {isAdding && <p className="text-xs text-muted-foreground">{t('common.loading')}</p>}

          {/* Member list */}
          {isLoadingMembers ? (
            <div className="space-y-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3 animate-pulse">
                  <div className="h-9 w-9 rounded-full bg-muted" />
                  <div className="flex-1 h-4 rounded bg-muted" />
                </div>
              ))}
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {members.map(member => {
                const profileId = member.profile?.entity?.id;
                const enriched = profileId ? profileMap.get(profileId) : undefined;
                const name = enriched?.displayName
                  || member.profile?.display_name
                  || profileId
                  || '?';
                const avatarUrl = enriched?.avatarUrl || '';
                const role = member.role ?? 'CONVERSATION_ROLE_MEMBER';
                const isOwner = role === 'CONVERSATION_ROLE_OWNER';
                const isEditingRole = editingRoleFor === member.entity?.id;

                return (
                  <li key={member.entity?.id} className="flex items-center gap-3 py-3">
                    <EntityAvatar name={name} image={avatarUrl} profileId={profileId} size="sm" className="h-9 w-9" />
                    <div className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-foreground">{name}</span>
                      {profileId && (
                        <Link
                          to={`/userprofiles/${profileId}`}
                          className="truncate text-xs text-muted-foreground hover:text-foreground"
                        >
                          {t('common.viewProfile')}
                        </Link>
                      )}
                    </div>

                    {isEditingRole ? (
                      <Select
                        value={role}
                        onChange={e => handleUpdateRole(member, e.target.value as ConversationRole)} onBlur={() => setEditingRoleFor(null)} options={editableRoles.map(r => ({ value: r.value, label: t(r.labelKey) }))} className="w-36" autoFocus />
                    ) : (
                      <button
                        onClick={() => !isOwner && setEditingRoleFor(member.entity?.id ?? null)}
                        disabled={isOwner}
                        className="disabled:cursor-default"
                      >
                        <Badge
                          size="sm"
                          variant={roleBadgeVariantMap[role] ?? 'default'}
                        >
                          {t(roleLabelKeyMap[role] ?? 'chat.role.member')}
                        </Badge>
                      </button>
                    )}

                    {!isOwner && (
                      <button
                        onClick={() => handleRemoveMember(member)}
                        className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-destructive"
                        title={t('common.remove')}
                      >
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* ---- Mention Tags ---- */}
      {!isDirect && (
        <Card>
          <CardHeader>
            <CardTitle>{t('chat.mentionTags.title')} ({mentionTags.length})</CardTitle>
            <CardDescription>{t('chat.mentionTags.manageDescription')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {isLoadingTags ? (
              <div className="space-y-2">
                {Array.from({ length: 2 }).map((_, i) => (
                  <div key={i} className="h-8 animate-pulse rounded bg-muted" />
                ))}
              </div>
            ) : mentionTags.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('chat.mentionTags.empty')}</p>
            ) : (
              <ul className="divide-y divide-border">
                {mentionTags.map(tag => (
                  <li key={tag.entity?.id} className="flex items-center justify-between py-2">
                    <div className="min-w-0">
                      <span className="text-sm font-medium text-foreground">@{tag.display_name || tag.name}</span>
                      {tag.description && <p className="truncate text-xs text-muted-foreground">{tag.description}</p>}
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge size="sm" variant="secondary">
                        {t('chat.mentionTags.subscriberCount', { count: tag.subscriber_count ?? 0 })}
                      </Badge>
                      <button
                        onClick={() => handleDeleteMentionTag(tag)}
                        className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-destructive"
                        title={t('common.delete')}
                      >
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
              <Input label={t('chat.mentionTags.namePlaceholder')} value={newTagName} onChange={e => setNewTagName(e.target.value)} className="flex-1" />
              <Input label={t('chat.mentionTags.descriptionPlaceholder')} value={newTagDescription} onChange={e => setNewTagDescription(e.target.value)} className="flex-1" />
              <Button onClick={handleCreateMentionTag} isLoading={isCreatingTag} disabled={!newTagName.trim()}>
                {t('chat.mentionTags.createTag')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ---- Danger Zone ---- */}
      <Card>
        <CardHeader>
          <CardTitle className="text-destructive">{t('chat.dangerZone')}</CardTitle>
          <CardDescription>{t('chat.dangerZoneDescription')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between rounded-lg border border-border p-4">
            <div>
              <p className="text-sm font-medium text-foreground">{t('chat.leaveConversation')}</p>
              <p className="text-xs text-muted-foreground">{t('chat.leaveDescription')}</p>
            </div>
            <Button variant="secondary" size="sm" onClick={handleLeave}>
              {t('chat.leave')}
            </Button>
          </div>

          {!conversation?.is_archived && (
            <div className="flex items-center justify-between rounded-lg border border-border p-4">
              <div>
                <p className="text-sm font-medium text-foreground">{t('chat.archiveConversation')}</p>
                <p className="text-xs text-muted-foreground">{t('chat.archiveDescription')}</p>
              </div>
              <Button variant="secondary" size="sm" onClick={handleArchive}>
                {t('chat.archive')}
              </Button>
            </div>
          )}

          <div className="flex items-center justify-between rounded-lg border border-destructive/30 p-4">
            <div>
              <p className="text-sm font-medium text-foreground">{t('chat.deleteConversation')}</p>
              <p className="text-xs text-muted-foreground">{t('chat.deleteDescription')}</p>
            </div>
            <Button variant="danger" size="sm" onClick={handleDelete}>
              {t('common.delete')}
            </Button>
          </div>
        </CardContent>
      </Card>

      <ConfirmDialog
        open={confirmDialog !== null}
        title={confirmDialog?.title ?? ''}
        description={confirmDialog?.description}
        confirmLabel={confirmDialog?.confirmLabel}
        destructive={confirmDialog?.destructive}
        isConfirming={isConfirming}
        onCancel={() => { if (!isConfirming) setConfirmDialog(null); }}
        onConfirm={async () => {
          if (!confirmDialog) return;
          setIsConfirming(true);
          try {
            await confirmDialog.onConfirm();
          } finally {
            setIsConfirming(false);
            setConfirmDialog(null);
          }
        }}
      />
    </div>
  );
});

// ---- Toggle Row Component ----

function ToggleRow({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-4">
      <div>
        <p className="text-sm font-medium text-foreground">{label}</p>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ${
          checked ? 'bg-primary' : 'bg-muted'
        }`}
      >
        <span
          className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-background shadow ring-0 transition-transform ${
            checked ? 'translate-x-5' : 'translate-x-0'
          }`}
        />
      </button>
    </div>
  );
}

export default ChatEditPage;
